import { TableIcon } from 'lucide-react'
import { useState } from 'react'
import type { SelectedEdge } from '@/components/neo4j/cytoscape/types'
import { RowsDialog } from '@/components/structured-ingest/RowsDialog'
import { Button } from '@/components/ui/button'
import { useEdgeRows } from './hooks/use-structured-ingest'
import { describeError } from './StructuredRowsDialog'
import { useRowsSelection } from './useRowsSelection'

const PAGE = 100

/**
 * "Source rows" on a projected (structured-ingest) edge: the aggregated edge is
 * a count and a sum; this opens the exact rows of the file it was built from.
 * Renders nothing for edges that did not come from a projection.
 */
export function EdgeRowsButton({ caseId, edge }: { caseId: string; edge: SelectedEdge }) {
  const [open, setOpen] = useState(false)
  const [offset, setOffset] = useState(0)
  const props = edge.properties ?? {}
  const projected = Boolean(props._projection_id)
  const rows = useEdgeRows(caseId, open && projected ? edge.id : null, offset, PAGE)
  // the page names its table (`<analysis>/<table>`), which is what promotion needs
  const selection = useRowsSelection(caseId, rows.data?.table ?? null)
  if (!projected) return null
  const count = typeof props.count === 'number' ? props.count : null
  const grain = typeof props._grain_key === 'string' && props._grain_key ? props._grain_key : null
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-9 gap-1.5 text-xs md:h-7"
        onClick={() => {
          setOffset(0)
          setOpen(true)
        }}
      >
        <TableIcon className="size-3.5" />
        Source rows{count !== null ? ` (${count.toLocaleString()})` : ''}
      </Button>
      <RowsDialog
        open={open}
        title={`${edge.sourceName ?? edge.source} —${edge.type}→ ${edge.targetName ?? edge.target}`}
        subtitle={grain ? `grain ${grain}` : null}
        page={rows.data ?? null}
        loading={rows.isFetching}
        error={rows.error ? describeError(rows.error) : null}
        selection={selection}
        onOpenChange={next => {
          if (!next) setOpen(false)
        }}
        onPage={setOffset}
      />
    </>
  )
}
